import React, { useEffect, useState } from 'react';
import Button from '../UI/Button';
import Card from '../UI/Card';
import { WalletSettings } from '../../api/services/platformSettings.service';

interface WalletTabProps {
  value: WalletSettings;
  onSave: (wallet: WalletSettings) => Promise<void>;
  saving: boolean;
}

const inputClass =
  'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-400';
const labelClass = 'block text-xs font-medium text-gray-600 mb-1';

const WalletTab: React.FC<WalletTabProps> = ({ value, onSave, saving }) => {
  const [form, setForm] = useState<WalletSettings>(value);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setForm(value);
    setDirty(false);
  }, [value]);

  const set = <K extends keyof WalletSettings>(k: K, v: WalletSettings[K]) => {
    setForm((p) => ({ ...p, [k]: v }));
    setDirty(true);
  };

  return (
    <div className="space-y-4">
      <Card>
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Wallet Settings</h2>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-900">Deposits / Credits</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Whether users can top up their balance.
              </p>
            </div>
            <button
              onClick={() => set('creditEnabled', !form.creditEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.creditEnabled ? 'bg-primary-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form.creditEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-900">Withdrawals</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Whether users can request withdrawals.
              </p>
            </div>
            <button
              onClick={() => set('withdrawalEnabled', !form.withdrawalEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.withdrawalEnabled ? 'bg-primary-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form.withdrawalEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className={labelClass}>Minimum Withdrawal Amount (ETB)</label>
            <input
              type="number"
              min={0}
              value={form.minWithdrawalAmount}
              onChange={(e) => set('minWithdrawalAmount', Number(e.target.value) || 0)}
              disabled={!form.withdrawalEnabled}
              className={`${inputClass} ${
                !form.withdrawalEnabled ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Users cannot request withdrawals below this amount.
            </p>
          </div>
        </div>
      </Card>

      {/* ⭐ NEW — Balance Transfer */}
      <Card>
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Balance Transfer</h2>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-900">Allow Transfers</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Whether users can send balance to another user by phone number.
              </p>
            </div>
            <button
              onClick={() => set('transferEnabled', !form.transferEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.transferEnabled ? 'bg-primary-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form.transferEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className={labelClass}>Minimum Transfer Amount (ETB)</label>
            <input
              type="number"
              min={0}
              value={form.minTransferAmount}
              onChange={(e) => set('minTransferAmount', Number(e.target.value) || 0)}
              disabled={!form.transferEnabled}
              className={`${inputClass} ${
                !form.transferEnabled ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Users cannot transfer below this amount.
            </p>
          </div>
        </div>
      </Card>

      <Card>
        <h2 className="text-sm font-semibold text-gray-900 mb-4">Signup Bonus</h2>

        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-100">
            <div>
              <p className="text-sm font-medium text-gray-900">Welcome Bonus</p>
              <p className="text-xs text-gray-500 mt-0.5">
                Give first-time users a starting balance when they join.
              </p>
            </div>
            <button
              onClick={() => set('signupBonusEnabled', !form.signupBonusEnabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                form.signupBonusEnabled ? 'bg-primary-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                  form.signupBonusEnabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div>
            <label className={labelClass}>Bonus Amount (ETB)</label>
            <input
              type="number"
              min={0}
              value={form.signupBonusAmount}
              onChange={(e) => set('signupBonusAmount', Number(e.target.value) || 0)}
              disabled={!form.signupBonusEnabled}
              className={`${inputClass} ${
                !form.signupBonusEnabled ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            />
            <p className="text-[10px] text-gray-500 mt-1">
              Amount credited once to each new user after they share their phone number.
            </p>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button size="sm" disabled={!dirty || saving} onClick={() => onSave(form)}>
          {saving ? 'Saving...' : 'Save Wallet'}
        </Button>
      </div>
    </div>
  );
};

export default WalletTab;